import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AutoComplete,
  Button,
  Col,
  DatePicker,
  Flex,
  Input,
  Modal,
  Row,
  Select,
  Space,
} from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { clientsActions } from "../../../../store/slices/clients-slices";
import { createOrderActions } from "../../../../store/slices/create-order-slice/create.orders.slice";
import { ordersActions } from "../../../../store/slices/orders-slices";
import { ordersService } from "../../../../services/orders-services/orders.service";
import { cycleMenuService } from "../../../../services/menu-services/cycleMenu.service";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { periodsActions } from "../../../../store/slices/settings-slices";
import { groupCustomDishes } from "../../../../helpers/groupCustomDishes";

function getCycleDay(date, startDate, lengthDays) {
  const diff = dayjs(date).diff(dayjs(startDate), "day");
  return ((diff % lengthDays) + lengthDays) % lengthDays + 1;
}

const paymentMethods = [
  { name: "Transfer", value: "Transfer" },
  { name: "Online", value: "Online" },
  { name: "Cash", value: "Cash" },
];

function LeftOrderCreateModel({
  setIsPacksLoading,
  setIsPromoCodeLoading,
  setIsBonusPointsLoading,
  isPacksLoading,
  isPromoCodeLoading,
  isBonusPointsLoading,
  refetchTrigger,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { menuFormat, cycleConfig } = useSelector((store) => store.appSettings);
  const {
    userId,
    userName,
    packId,
    packName,
    daysOrdered,
    selectedDates,
    paymentMethod,
    periodId,
    userPoints,
    appliedPromoCode,
    appliedPromoCodeId,
    bonusPointsToUse,
    pricingLoading,
    payableAmount,
    basePrice,
    bonusPoints,
    finalDaysOrdered,
    additionalDaysToAdd,
    periodDiscountAmount,
    bonusDiscountApplied,
    promoDiscountAmount,
    replacedDishes,
    replacedProteinDishes,
    replacedSauceDishes,
    isCustomMode,
    customDishes,
  } = useSelector((store) => store.createOrder);
  const [disableSaturday, setDisableSaturday] = useState(false);
  const [disableSunday, setDisableSunday] = useState(false);
  // promo code input
  const [promoCodeValue, setPromoCodeValue] = useState("");
  // bonus points input
  const [bonusPointsValue, setBonusPointsValue] = useState("");
  const maxBonusPoints = useMemo(() => userPoints, [userPoints]);
  // client select
  const { searchClients } = useSelector((store) => store.clients);
  const options = useMemo(
    () =>
      searchClients?.users?.map((user) => ({
        label: user.clientName,
        value: user.id,
        points: user.points,
      })),
    [searchClients]
  );

  const handleSearch = (value) => {
    dispatch(createOrderActions.setUserName(value));
    dispatch(clientsActions.getSearchClients({ page: 1, word: value }));
  };

  const onSelect = (value, option) => {
    dispatch(createOrderActions.setUserId(value));
    dispatch(createOrderActions.setUserName(option.label));
    dispatch(createOrderActions.setUserPoints(option.points));
  };

  // pack select
  const { menuDishesPacksForDates } = useSelector((store) => store.orders);
  const packs = useMemo(
    () => menuDishesPacksForDates?.[0]?.packs || [],
    [menuDishesPacksForDates]
  );
  const optionsPacks = useMemo(
    () =>
      packs.map((pack) => ({
        label: pack.name.en,
        value: pack.id,
      })),
    [packs]
  );

  const onSelectPack = (value, option) => {
    dispatch(createOrderActions.setPackId(value));
    dispatch(createOrderActions.setPackName(option.label));
    dispatch(createOrderActions.setReplacedDishes([]));

    // Detect custom pack (pricePerDay === 0 or name contains "custom")
    const selectedPack = packs.find((p) => p.id === value);
    const isCustom =
      selectedPack?.pricePerDay === 0 ||
      selectedPack?.name?.en?.toLowerCase().includes("custom");
    dispatch(createOrderActions.setIsCustomMode(!!isCustom));
    dispatch(createOrderActions.clearCustomDishes());
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsPacksLoading(true);

        if (menuFormat === "period_based") {
          const formattedDates = selectedDates.map((d) =>
            dayjs(d).format("YYYY-MM-DD")
          );

          // Group dates by their cycle day to avoid duplicate fetches
          const cycleDayToDateMap = new Map();
          formattedDates.forEach((date) => {
            const cycleDay = cycleConfig?.startDate
              ? getCycleDay(date, cycleConfig.startDate, cycleConfig.lengthDays || 7)
              : 1;
            if (!cycleDayToDateMap.has(cycleDay)) {
              cycleDayToDateMap.set(cycleDay, []);
            }
            cycleDayToDateMap.get(cycleDay).push(date);
          });

          const results = [];
          for (const [cycleDay, dates] of cycleDayToDateMap.entries()) {
            const packs = await cycleMenuService.getByDay(cycleDay);
            const mappedPacks = (packs || []).map((pack) => ({
              ...pack,
              menuDishes: pack.cycleDayMenuDishes || [],
            }));
            dates.forEach((date) => {
              results.push({ date, packs: mappedPacks });
            });
          }

          dispatch(ordersActions.changeMenuDishesPacksForDates(results));
        } else {
          const res = await ordersService.getAllMenuDishesForDate(
            selectedDates.map((date) => dayjs(date).format("YYYY-MM-DD"))
          );
          dispatch(ordersActions.changeMenuDishesPacksForDates(res.data));
        }
      } catch (error) {
        console.error(t("modal-toast-fetch-error"), error);
        toast.error(t("modal-toast-fetch-error"));
      } finally {
        setIsPacksLoading(false);
      }
    };
    fetchData();
  }, [selectedDates, menuFormat, refetchTrigger]);

  // days ordered
  const onChangeDaysOrdered = (dates) => {
    dispatch(
      createOrderActions.setSelectedDates(
        dates.map((date) => dayjs(date).toISOString())
      )
    );
    dispatch(createOrderActions.setDaysOrdered(dates.length));
  };

  const disabledDate = (current) => {
    return (
      current && new Date(current) < new Date(new Date().setHours(0, 0, 0, 0))
    );
  };

  // period select
  const { periods } = useSelector((store) => store.periods);
  useEffect(() => {
    dispatch(periodsActions.getAllPeriods());
  }, []);

  // get eligible period
  useEffect(() => {
    const eligiblePeriods = periods.filter(
      (period) => period.daysCount <= daysOrdered
    );
    const bestPeriod =
      eligiblePeriods.length > 0
        ? eligiblePeriods.sort((a, b) => b.daysCount - a.daysCount)[0]
        : null;
    dispatch(createOrderActions.setPeriodId(bestPeriod?.id));
  }, [daysOrdered, periods, dispatch]);

  // promo code input
  const handleApplyPromoCode = async (promoCode) => {
    if (!promoCode.trim()) {
      toast.error(t("Please enter a promo code"));
      return;
    }
    try {
      setIsPromoCodeLoading(true);
      const requestData = {
        promoCode: promoCode.trim(),
        userId: userId,
        packId: packId,
        daysOrdered: daysOrdered,
        periodId: periodId,
      };
      const res = await ordersService.checkPromoWithOrder(requestData);
      dispatch(createOrderActions.setAppliedPromoCode(res));
      dispatch(
        createOrderActions.setAppliedPromoCodeId(res.promoCodeId || null)
      );
      toast.success(t("Promo code applied successfully"));
    } catch (error) {
      console.error(t("Promo code application error"), error);
      toast.error(t("Promo code application error"));
    } finally {
      setIsPromoCodeLoading(false);
    }
  };

  const handleRemovePromoCode = () => {
    dispatch(createOrderActions.setAppliedPromoCode(null));
    dispatch(createOrderActions.setAppliedPromoCodeId(null));
    setPromoCodeValue("");
    setIsPromoCodeLoading(false);
    toast.success(t("Promo code removed successfully"));
  };

  // bonus points input
  const handleApplyBonusPoints = async (bonusPointsToUse) => {
    dispatch(createOrderActions.setBonusPointsToUse(Number(bonusPointsToUse)));
  };

  // Build meals array from custom dishes for pricing/submission
  const getCustomMealsForDates = () => {
    // Get the custom pack's first meal ID
    const customPack = packs.find((p) => p.id === packId);
    const customMealId = customPack?.meals?.[0]?.id || "";

    const meals = [];
    Object.entries(customDishes).forEach(([date, dishesForDate]) => {
      const groups = groupCustomDishes(dishesForDate);
      groups.forEach((group) => {
        const mainDish = group.main.dish;
        const proteinSub = group.subDishes.find((s) => s.dish.type === "PROTEIN");
        const sauceSub = group.subDishes.find((s) => s.dish.type === "SAUCE");

        meals.push({
          date,
          mealId: customMealId,
          dishId: mainDish.id,
          type: mainDish.type || undefined,
          proteinDishId: proteinSub?.dish?.id || undefined,
          sauceDishId: sauceSub?.dish?.id || undefined,
        });
      });
    });
    return meals;
  };

  // get menu dishes for dates
  const getMenuDishesForDates = () => {
    if (!packId) return [];

    // Custom mode: build meals from customDishes
    if (isCustomMode) {
      return getCustomMealsForDates();
    }

    const menuDishes = [];
    menuDishesPacksForDates.forEach(({ date, packs }) => {
      const pack = packs?.find((p) => p.id === packId);
      const formattedDate = dayjs(date).format("YYYY-MM-DD");

      for (const dish of pack?.menuDishes || []) {
        const isReplaced = replacedDishes.find(
          (replacedDish) =>
            replacedDish.mealId === dish.mealId &&
            dayjs(replacedDish.date).format("YYYY-MM-DD") === formattedDate &&
            replacedDish.packId === packId
        );

        // Check for protein/sauce replacements
        const proteinReplacement = replacedProteinDishes.find(
          (r) =>
            r.mealId === dish.mealId &&
            dayjs(r.date).format("YYYY-MM-DD") === formattedDate &&
            r.packId === packId
        );
        const sauceReplacement = replacedSauceDishes.find(
          (r) =>
            r.mealId === dish.mealId &&
            dayjs(r.date).format("YYYY-MM-DD") === formattedDate &&
            r.packId === packId
        );

        const mealEntry = {
          date: date,
          mealId: dish.mealId,
          dishId: isReplaced ? isReplaced.replacedTo : dish.dish.id,
          type: dish.type || undefined,
          proteinDishId: proteinReplacement
            ? proteinReplacement.replacedTo
            : dish.proteinDish?.id || undefined,
          sauceDishId: sauceReplacement
            ? sauceReplacement.replacedTo
            : dish.sauceDish?.id || undefined,
        };

        menuDishes.push(mealEntry);
      }
    });

    return menuDishes;
  };

  // get pricing and discount
  const getPricing = async () => {
    try {
      dispatch(createOrderActions.setPricingLoading(true));

      const eligiblePeriods = periods.filter(
        (period) => period.daysCount <= daysOrdered
      );
      const bestPeriod =
        eligiblePeriods.length > 0
          ? eligiblePeriods.sort((a, b) => b.daysCount - a.daysCount)[0]
          : null;

      const menuDishes = getMenuDishesForDates(selectedDates);

      const requestData = {
        userId: userId,
        packId: packId,
        daysOrdered: daysOrdered,
        periodId: bestPeriod?.id || null,
        meals: menuDishes,
      };

      if (appliedPromoCodeId) {
        requestData.promoCodeId = appliedPromoCodeId;
        requestData.promoCode = promoCodeValue || null;
      }

      if (bonusPointsToUse > 0) {
        requestData.bonusDiscount = bonusPointsToUse;
      }

      const res = await ordersService.calculatePricing(requestData);
      dispatch(createOrderActions.setPricing(res));
    } catch (error) {
      console.error(t("Failed to get pricing and discount"), error);
      toast.error(t("Failed to get pricing and discount"));
    } finally {
      dispatch(createOrderActions.setPricingLoading(false));
    }
  };

  // Debounce timer for custom mode pricing
  const pricingTimerRef = useRef(null);

  useEffect(() => {
    if (!userId || !packId || !daysOrdered) {
      return;
    }

    // Debounce pricing calls in custom mode to avoid rapid API calls
    if (isCustomMode) {
      if (pricingTimerRef.current) clearTimeout(pricingTimerRef.current);
      pricingTimerRef.current = setTimeout(() => {
        getPricing();
      }, 400);
      return () => {
        if (pricingTimerRef.current) clearTimeout(pricingTimerRef.current);
      };
    }

    getPricing();
  }, [
    userId,
    packId,
    daysOrdered,
    appliedPromoCodeId,
    bonusPointsToUse,
    replacedDishes,
    replacedProteinDishes,
    replacedSauceDishes,
    menuDishesPacksForDates,
    customDishes,
    isCustomMode,
  ]);

  return (
    <Col
      xs={24}
      md={8}
      lg={8}
      xl={8}
      xxl={8}
      style={{ maxHeight: "600px", overflowY: "auto" }}
    >
      {/* client select */}
      <div>
        <label>{t("client")}</label>
        <AutoComplete
          popupMatchSelectWidth={252}
          style={{ width: "100%" }}
          options={options}
          onSelect={onSelect}
          onSearch={handleSearch}
          value={userName}
        >
          <Input.Search size="large" placeholder="input here" enterButton />
        </AutoComplete>
      </div>

      {/* pack select */}
      <div>
        <label>{t("Menu")}</label>
        <AutoComplete
          popupMatchSelectWidth={252}
          style={{ width: "100%" }}
          options={optionsPacks}
          onSelect={onSelectPack}
          value={packName}
          showSearch
          loading={isPacksLoading}
        ></AutoComplete>
      </div>

      {/* days ordered */}
      <div>
        <label>{t("days ordered")}</label>
        {/* Preset periods select (e.g. 5 days, 10 days, etc.) */}
        <Select
          style={{ width: "100%", marginBottom: 8 }}
          size="large"
          placeholder={t("Select period")}
          options={
            periods?.map((period) => ({
              label: `${period.daysCount} ${t("days")}${
                period.discount ? ` (${period.discount}% ${t("discount")})` : ""
              }`,
              value: period.daysCount,
            })) || []
          }
          value={
            periods && selectedDates?.length ? selectedDates.length : undefined
          }
          onChange={(daysCount) => {
            // find today or next available start
            const today = dayjs().add(1, "day");
            const newSelectedDates = [];
            for (let i = 0; i < daysCount; i++) {
              newSelectedDates.push(today.add(i, "day").format("YYYY-MM-DD"));
            }
            onChangeDaysOrdered(newSelectedDates.map((date) => dayjs(date)));
          }}
          allowClear
        />
        <div
          style={{
            marginBottom: 8,
            display: "flex",
            gap: 16,
            alignItems: "center",
          }}
        >
          <label>
            <input
              type="checkbox"
              checked={disableSaturday}
              onChange={(e) => {
                setDisableSaturday(e.target.checked);
                if (e.target.checked) {
                  // Remove all Saturdays from selectedDates
                  const newSelected = selectedDates.filter((dateStr) => {
                    const d = dayjs(dateStr);
                    return d.day() !== 6;
                  });
                  if (newSelected.length !== selectedDates.length) {
                    onChangeDaysOrdered(newSelected.map((date) => dayjs(date)));
                  }
                }
              }}
              style={{ marginRight: 4 }}
            />
            {t("Disable Saturdays")}
          </label>
          <label>
            <input
              type="checkbox"
              checked={disableSunday}
              onChange={(e) => {
                setDisableSunday(e.target.checked);
                if (e.target.checked) {
                  // Remove all Sundays from selectedDates
                  const newSelected = selectedDates.filter((dateStr) => {
                    const d = dayjs(dateStr);
                    return d.day() !== 0;
                  });
                  if (newSelected.length !== selectedDates.length) {
                    onChangeDaysOrdered(newSelected.map((date) => dayjs(date)));
                  }
                }
              }}
              style={{ marginRight: 4 }}
            />
            {t("Disable Sundays")}
          </label>
        </div>
        <DatePicker
          multiple
          onChange={onChangeDaysOrdered}
          maxTagCount="responsive"
          value={selectedDates.map((date) => dayjs(date))}
          size="large"
          disabledDate={(current) => {
            // Exclude disabled Sat/Sun
            const baseDisabled =
              typeof disabledDate === "function"
                ? disabledDate(current)
                : false;
            if (disableSaturday && current.day() === 6) return true;
            if (disableSunday && current.day() === 0) return true;
            return baseDisabled;
          }}
        />
      </div>

      {/* payment method select */}
      <div>
        <label>{t("payment method")}</label>
        <Select
          style={{ width: "100%" }}
          size="large"
          options={paymentMethods.map((method) => ({
            label: method.name,
            value: method.name,
          }))}
          value={paymentMethod}
          onChange={(value) => {
            dispatch(createOrderActions.setPaymentMethod(value));
          }}
        />
      </div>

      {userId && (
        <>
          {/* promo code input */}
          <div style={{ marginBottom: 8 }}>
            <label>{t("promo code")}</label>
            <Space.Compact style={{ width: "100%" }}>
              <Input
                placeholder={t("Enter promo code")}
                value={promoCodeValue}
                onChange={(e) => setPromoCodeValue(e.target.value)}
                disabled={isPromoCodeLoading}
              />
              {appliedPromoCode ? (
                <Button
                  type="primary"
                  onClick={() => handleRemovePromoCode()}
                  loading={isPromoCodeLoading}
                >
                  {t("Remove promo code")}
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={() => handleApplyPromoCode(promoCodeValue)}
                  loading={isPromoCodeLoading}
                >
                  {t("Apply promo code")}
                </Button>
              )}
            </Space.Compact>
          </div>

          {/* bonus points input */}
          <div style={{ marginBottom: 8 }}>
            <label>
              {t("bonus points")}
              <span style={{ color: "var(--el-color-info-dark-2)" }}>
                ({maxBonusPoints} {t("points")})
              </span>
              <span style={{ color: "var(--el-color-info-dark-2)" }}>
                {t("Available")}: {maxBonusPoints} {t("points")}
              </span>
            </label>
            <Space.Compact style={{ width: "100%" }}>
              <Input
                type="number"
                min="0"
                max={userPoints.toFixed(0)}
                placeholder={t("Enter bonus points")}
                value={bonusPointsValue}
                onChange={(e) => setBonusPointsValue(e.target.value)}
                disabled={isBonusPointsLoading}
              />
              <Button
                type="primary"
                onClick={() => handleApplyBonusPoints(bonusPointsValue)}
                loading={isBonusPointsLoading}
              >
                {t("Apply bonus points")}
              </Button>
            </Space.Compact>
          </div>

          {/* pricing and discount */}
          <div
            style={{
              background: "#f7f8fa",
              borderRadius: 8,
              padding: "20px 24px",
              marginBottom: 16,
              boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
            }}
          >
            <label
              style={{
                fontWeight: 600,
                fontSize: 16,
                display: "block",
                marginBottom: 14,
              }}
            >
              {t("Pricing and discount")}
            </label>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 20,
                marginBottom: 8,
              }}
            >
              <div style={{ minWidth: 150 }}>
                <span style={{ color: "#888" }}>{t("Base price")}:</span>{" "}
                <span style={{ fontWeight: 500 }}>{basePrice}</span>
              </div>
              <div style={{ minWidth: 180 }}>
                <span style={{ color: "#888" }}>{t("Promo discount")}:</span>{" "}
                <span style={{ color: "#36b37e", fontWeight: 500 }}>
                  -{promoDiscountAmount || 0}
                </span>
              </div>
              <div style={{ minWidth: 170 }}>
                <span style={{ color: "#888" }}>{t("Bonus discount")}:</span>{" "}
                <span style={{ color: "#ffd700", fontWeight: 500 }}>
                  -{bonusDiscountApplied || 0}
                </span>
              </div>
              <div style={{ minWidth: 180 }}>
                <span style={{ color: "#888" }}>{t("Period discount")}:</span>{" "}
                <span style={{ color: "#ff6633", fontWeight: 500 }}>
                  -{periodDiscountAmount || 0}
                </span>
              </div>
              <div style={{ minWidth: 150 }}>
                <span style={{ color: "#888" }}>{t("Final price")}:</span>{" "}
                <span
                  style={{
                    color: "#0050b3",
                    fontWeight: 700,
                    fontSize: 17,
                  }}
                >
                  {payableAmount}
                </span>
              </div>
            </div>
            <div style={{ borderTop: "1px solid #eee", paddingTop: 8 }}>
              <span style={{ color: "#888" }}>{t("Bonus points")}:</span>{" "}
              <span style={{ color: "#0050b3", fontWeight: 500 }}>
                {bonusPoints}
              </span>
            </div>
          </div>
        </>
      )}
    </Col>
  );
}

export default LeftOrderCreateModel;
